import {FC, useEffect, useState} from 'react';
import {AxiosError} from "axios";

import usedIcon from '../../assets/icons/used-sub.svg';
import availableIcon from '../../assets/icons/available-sub.svg';

import { Skeleton } from 'primereact/skeleton';
import styles from './subscriptions.module.scss';
import {useMutation} from "@tanstack/react-query";
import {extractErrorMessage} from "../../helpers/extractErrorMessage";
import {requests} from "../../services/http-common";
import {useToast} from "../../hooks/useToast";
import ButtonPrimary from "../../uiComponents/ButtonPrimary/ButtonPrimary";
import EmbeddedPayment from "../../components/DocumentGeneration/components/EmbeddedPayment/EmbeddedPayment";

interface TSubscriptionsProps {
    companyId: number;
    isAxonCompany:boolean;
}

const Subscriptions: FC<TSubscriptionsProps> = ({
    isAxonCompany,
    companyId,
}) => {

    const { show } = useToast();
    const [quota, setQuota] = useState<number>(0);
    const [isLoaded, setIsLoaded] = useState(false);
    const [itemPrice, setItemPrice] = useState<number>(0);
    const [clientSecret, setClientSecret] = useState('');
    const [usedOrders, setUsedOrders] = useState<number | null>(null);
    const [availableOrders, setAvailableOrders] = useState<number | null>(null);
    const [allowsQuotaSelection, setAllowsQuotaSelection] = useState<boolean>(false);

    const getEmbeddedPaymentUrl = useMutation({
        onError: (error: AxiosError<{ message: string }>, variables) => {
            const errorData = {
                error: error,
                variables: variables,
                retryFn: getEmbeddedPaymentUrl.mutate,
                show: show,
                cb: () => {}
            };
            extractErrorMessage(errorData);
        },
        onSuccess: (res) => {
            const embeddedData = res?.embeddedData;
            const clientSecret = embeddedData?.clientSecret;
            setClientSecret(clientSecret);
        },
        mutationFn: async () => await requests.get(`/api/checkout/${companyId}/create-company-session`),
    });
    const getEmbeddedPaymentUrlLoading = getEmbeddedPaymentUrl?.isPending;

    const getEmbeddedPaymentUrlWithQuota = useMutation({
        onError: (error: AxiosError<{ message: string }>, variables) => {
            const errorData = {
                error: error,
                variables: variables,
                retryFn: getEmbeddedPaymentUrl.mutate,
                show: show,
                cb: () => {}
            };
            extractErrorMessage(errorData);
        },
        onSuccess: (res) => {
            const embeddedData = res?.embeddedData;
            const clientSecret = embeddedData?.clientSecret;
            setClientSecret(clientSecret);
        },
        mutationFn: async (quota: number) => await requests.post(`/api/checkout/${companyId}/create-company-quota-session`, {
            quota,
            formType: "FILE_83B"
        }),
    });

    const getUrl = () => {
        if (allowsQuotaSelection) {
            getEmbeddedPaymentUrlWithQuota.mutate(quota);
        } else {
            getEmbeddedPaymentUrl.mutate();
        }
    }
    const closePayment = () => {
        setClientSecret('');
    }
    const inCrease = () => {
        setQuota((prev => prev + 1));
    }

    const deCrease = () => {
        setQuota((prev => {
            if (prev === 1) return prev;
            return prev - 1;
        }));
    }


useEffect(() => {
    let isMounted = true;

    requests.get(`/api/companies/${companyId}/subscription`)
        .then((response) => {
            const {
                FILE_83B:{
                    quota,
                    price,
                    allowedLimit,
                    remainingCount,
                    allowsQuotaSelection,
                }
            } = response.formPricing;
            setQuota(quota);
            setItemPrice(price);
            setAvailableOrders(remainingCount);
            setUsedOrders(allowedLimit - remainingCount);
            setAllowsQuotaSelection(allowsQuotaSelection);
        })
        .catch((error: AxiosError<{ detail: string }>) => {
            show({
                severity: 'error',
                detail: error?.response?.data?.detail || 'Failed to load company information',
            });
        })
        .finally(() => {
            if (isMounted) setIsLoaded(true);
        });

    return () => {
        isMounted = false;
        setAvailableOrders(null);
        setUsedOrders(null);
        setIsLoaded(false);
    };
}, [companyId]);

  return (
    <div className={styles.subscriptions}>
        <div className={styles.grid}>
            <div className={styles.item}>
                <div className={styles.row}>
                    <img src={availableIcon} alt=""/>
                    <span>Available Orders</span>
                </div>
                <p>
                    {
                        isLoaded ? (
                            availableOrders 
                        ) : (
                            <Skeleton height="2rem" className="mb-2" />
                        )}
                </p>
            </div>
            <div className={styles.item}>
                <div className={styles.row}>
                    <img src={usedIcon} alt=""/>
                    <span>Used Orders</span>
                </div>
                <p>
                    {
                        isLoaded ? (
                            usedOrders 
                        ) : (
                            <Skeleton height="2rem" className="mb-2" />
                        )}
                </p>
            </div>            
        </div>
        {
            allowsQuotaSelection && (
                <div className={styles.quotaSelection}>
                    <div className={styles.controlCol}>
                        <button type="button" disabled={quota === 1} onClick={deCrease}>-</button>
                        <span>{quota}</span>
                        <button type="button" onClick={inCrease}>+</button>
                    </div>
                    <p className={styles.price}>{`Item price: ${itemPrice}$`}</p>
                </div>
            )
        }

        {
            !isAxonCompany ? (
                <div className={styles.payButtonWrapper}>
                    <ButtonPrimary
                        label="Pay"
                        styleType="primaryOutline"
                        onClick={getUrl}
                        disabled={getEmbeddedPaymentUrlLoading}
                    />
                </div>
            ) : null
        }
        {
            clientSecret && (
                <>
                    <button className={styles.closeBtn} onClick={closePayment}>
                        <i className="bi bi-x"/>
                    </button>
                    <EmbeddedPayment clientSecret={clientSecret}/>
                </>
            )
        }
    </div>
  );
};

export default Subscriptions;
